import {
  isActiveDefectMapLayerId,
  normalizeActiveDefectMapLayerId,
} from './activeLayerMapId';

export interface StackOrderMapLayer {
  readonly id?: string;
  readonly type?: string;
  readonly declaredClass?: string;
}

export interface StackOrderMapLayers {
  readonly length: number;
  indexOf?: (layer: unknown) => number;
  getItemAt?: (index: number) => StackOrderMapLayer | undefined;
  toArray?: () => ReadonlyArray<StackOrderMapLayer>;
}

export interface StackOrderMap {
  readonly layers: StackOrderMapLayers;
  reorder: (layer: unknown, index: number) => void;
}

export type PlaceBelowOverlaysOptions = {
  /**
   * Extra map layers that must stay above survey WMTS / ortho imagery
   */
  readonly layersAboveImagery?: readonly unknown[];
};

export function stripSdmOverviewMapLayerSuffix(mapLayerId: string): string {
  return mapLayerId.replace(/__overview__[\w.:-]+$/, '');
}

export function isSdmMapOverlayLayerId(
  mapLayerId: string | null | undefined,
  activeLayerIds: readonly string[],
): boolean {
  if (!mapLayerId) {
    return false;
  }
  const withoutOverview = stripSdmOverviewMapLayerSuffix(mapLayerId);
  return isActiveDefectMapLayerId(withoutOverview, activeLayerIds);
}

export function isSurveyWmtsLayer(layer: StackOrderMapLayer | null | undefined): boolean {
  if (!layer) {
    return false;
  }
  if (typeof layer.type === 'string' && layer.type.toLowerCase() === 'wmts') {
    return true;
  }
  if (
    typeof layer.declaredClass === 'string' &&
    layer.declaredClass.toLowerCase().includes('wmtslayer')
  ) {
    return true;
  }
  return false;
}

function listMapLayers(layers: StackOrderMapLayers): readonly StackOrderMapLayer[] {
  if (typeof layers.toArray === 'function') {
    return layers.toArray();
  }
  const items: StackOrderMapLayer[] = [];
  for (let index = 0; index < layers.length; index += 1) {
    const item = layers.getItemAt?.(index);
    if (item) {
      items.push(item);
    }
  }
  return items;
}

export function findLowestSdmOverlayIndex(
  map: StackOrderMap,
  activeLayerIds: readonly string[],
): number | null {
  if (activeLayerIds.length === 0) {
    return null;
  }

  const items = listMapLayers(map.layers);
  let lowest: number | null = null;

  for (let index = 0; index < items.length; index += 1) {
    const layerId = items[index]?.id;
    if (!isSdmMapOverlayLayerId(layerId, activeLayerIds)) {
      continue;
    }
    if (lowest === null || index < lowest) {
      lowest = index;
    }
  }

  return lowest;
}

export function findImageryCeilingIndex(
  map: StackOrderMap,
  activeLayerIds: readonly string[],
  layersAboveImagery: readonly unknown[] = [],
  excludeLayer?: unknown,
): number | null {
  let ceiling = findLowestSdmOverlayIndex(map, activeLayerIds);
  const layers = map.layers;

  for (const above of layersAboveImagery) {
    if (!above || above === excludeLayer) {
      continue;
    }
    const index =
      typeof layers.indexOf === 'function' ? layers.indexOf(above) : -1;
    if (index < 0) {
      continue;
    }
    if (ceiling === null || index < ceiling) {
      ceiling = index;
    }
  }

  return ceiling;
}

export function placeLayerBelowSdmOverlays(
  map: StackOrderMap,
  layer: unknown,
  activeLayerIds: readonly string[],
  options?: PlaceBelowOverlaysOptions,
): boolean {
  if (!map || typeof map.reorder !== 'function') {
    return false;
  }

  const layers = map.layers;
  const currentIndex =
    typeof layers.indexOf === 'function' ? layers.indexOf(layer) : -1;
  if (currentIndex < 0) {
    return false;
  }

  const ceilingIndex = findImageryCeilingIndex(
    map,
    activeLayerIds,
    options?.layersAboveImagery ?? [],
    layer,
  );
  if (ceilingIndex === null) {
    return false;
  }

  if (currentIndex < ceilingIndex) {
    const targetIndex = ceilingIndex - 1;
    if (currentIndex === targetIndex) {
      return false;
    }
    // Move up within the imagery band to just under the ceiling.
    map.reorder(layer, targetIndex);
    return true;
  }

  // Sitting on/above the ceiling — push down to the ceiling index.
  if (currentIndex > ceilingIndex) {
    map.reorder(layer, ceilingIndex);
    return true;
  }

  return false;
}

export function bringSurveyLayerForward(
  map: StackOrderMap,
  layer: StackOrderMapLayer,
  activeLayerIds: readonly string[],
  options?: {
    readonly forceWmts?: boolean;
    readonly layersAboveImagery?: readonly unknown[];
  },
): void {
  if (!map || typeof map.reorder !== 'function') {
    return;
  }

  const layers = map.layers;
  const currentIndex =
    typeof layers.indexOf === 'function' ? layers.indexOf(layer) : -1;
  if (currentIndex < 0) {
    return;
  }

  const layersAboveImagery = options?.layersAboveImagery ?? [];
  const treatAsWmts = options?.forceWmts === true || isSurveyWmtsLayer(layer);
  const ceilingExtras = treatAsWmts ? layersAboveImagery : [];
  const ceilingIndex = findImageryCeilingIndex(
    map,
    activeLayerIds,
    ceilingExtras,
    layer,
  );
  const moved = placeLayerBelowSdmOverlays(map, layer, activeLayerIds, {
    layersAboveImagery: ceilingExtras,
  });
  if (moved) {
    return;
  }
  if (ceilingIndex !== null) {
    nudgeLayerInBand(map, layer, currentIndex, ceilingIndex);
    return;
  }
  if (activeLayerIds.length > 0 || ceilingExtras.length > 0) {
    return;
  }

  const targetIndex = Math.max(0, layers.length - 1);
  if (currentIndex === targetIndex) {
    nudgeLayerInBand(map, layer, currentIndex, null);
    return;
  }
  map.reorder(layer, targetIndex);
}

function nudgeLayerInBand(
  map: StackOrderMap,
  layer: StackOrderMapLayer,
  currentIndex: number,
  ceilingIndex: number | null,
): void {
  const layers = map.layers;
  if (layers.length <= 1) {
    return;
  }
  const below = currentIndex - 1;
  const above = currentIndex + 1;
  const canNudgeBelow =
    below >= 0 && (ceilingIndex === null || below < ceilingIndex);
  const canNudgeAbove =
    above < layers.length &&
    (ceilingIndex === null || above < ceilingIndex);
  const nudgeIndex = canNudgeBelow ? below : canNudgeAbove ? above : -1;
  if (nudgeIndex < 0) {
    return;
  }
  map.reorder(layer, nudgeIndex);
  map.reorder(layer, currentIndex);
}

export function normalizeSdmOverlayMapLayerId(
  mapLayerId: string | null | undefined,
): string | null {
  if (!mapLayerId) {
    return null;
  }
  return normalizeActiveDefectMapLayerId(stripSdmOverviewMapLayerSuffix(mapLayerId));
}
